/**
 * t641u: the task made from a message, as one compact row attached under the message like a reply:
 * "<Person> turned this into a task · TASK-2 · title · ● Status". The status is live (realtime and polling refresh
 * the task links) and shown as a coloured dot with plain text, never struck through. Click opens the task in the
 * sidebar. Replaces the chip inside the bubble and the old "turned this into a task" system note.
 */
import type React from 'react';
import { CornerDownRight } from 'lucide-react';
import clsx from 'clsx';
import { taskRowLabel, taskRowLine, type MessageTaskLink } from '@tradeoutlet/shared';
import { useAuth } from '@/lib/auth';
import { useMembers } from '@/lib/hooks';
import { useUi } from '@/lib/store';

export function MessageTaskRow({ link, className }: { link: MessageTaskLink; className?: string }) {
  const openTask = useUi(s => s.openTask);
  const { userId } = useAuth();
  const { byId } = useMembers();
  const line = taskRowLine(link, userId, id => byId.get(id)?.display_name);
  return (
    <button type="button" onClick={() => openTask(link.task_id)} data-testid="message-task-row" aria-label={taskRowLabel(line, link)}
      title={link.no_project ? link.title : `${link.project_name} · ${link.title}`}
      className={clsx('flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border-l-[3px] border-accent-line bg-accent-soft py-1.5 pl-2 pr-2.5 text-left text-xs',
        'hover:bg-accent-line/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:bg-slate-800 dark:hover:bg-slate-700', className)}>
      <span className="flex shrink-0 items-center gap-1 text-slate-600 dark:text-slate-300" data-testid="message-task-row-line">
        <CornerDownRight className="h-3.5 w-3.5" aria-hidden />{line}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="shrink-0 font-mono font-semibold text-slate-500 dark:text-slate-400">{link.key}</span>
        <span className="min-w-0 truncate font-medium text-slate-900 dark:text-slate-100" data-testid="message-task-row-title">{link.title}</span>
        <span className="data-text flex shrink-0 items-center gap-1 font-semibold" style={{ '--badge': link.status_color } as React.CSSProperties} data-testid="message-task-row-status">
          <span className="h-2 w-2 rounded-full" style={{ background: link.status_color }} aria-hidden />{link.status_name}
        </span>
      </span>
    </button>
  );
}
